import { useEffect, useState } from 'react'
import { BookOpen, ChevronRight, FileText } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'

type DocFile = {
  id: string
  title: string
  description: string
  filename: string
}

const DOCS: DocFile[] = [
  {
    id: 'admin-reports',
    title: 'Admin Reports',
    description: 'Ask database questions and inspect saved reports in your dashboard environment.',
    filename: 'admin-reports-guide.md'
  },
  {
    id: 'tour-operator-testing',
    title: 'Tour Operator Test Account Guide',
    description: 'Create a TourAdmin account, link it to an operator, and test the operator dashboard.',
    filename: 'tour-operator-testing-guide.md'
  },
  {
    id: 'groups-admin',
    title: 'Groups Administration Guide',
    description: 'Complete guide to creating, managing, and moderating groups in HikeStrong.',
    filename: 'groups-administration-guide.md'
  },
  {
    id: 'figma-design',
    title: 'Figma Design Files',
    description: 'Access the official HikeStrong design system, UI components, and screen mockups.',
    filename: 'figma-design.md'
  },
  {
    id: 'string-table-schema',
    title: 'String Table Schema',
    description: 'Schema and conventions for localized string tables used across the app.',
    filename: 'string-table-schema.md'
  },
  {
    id: 'unit-system-architecture',
    title: 'Unit System Architecture',
    description: 'Architecture for metric/imperial unit conversion and display throughout the app.',
    filename: 'unit-system-architecture.md'
  }
]

export const DocsPage = () => {
  const [selectedDoc, setSelectedDoc] = useState<DocFile | null>(null)
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedDoc) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: clear content when no doc selected
      setContent('')
      return
    }

    setLoading(true)
    setError(null)

    fetch(`/docs/${selectedDoc.filename}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load document')
        return res.text()
      })
      .then((text) => setContent(text))
      .catch((err) => {
        console.error('Failed to load doc:', err)
        setError('Failed to load document. Please try again.')
      })
      .finally(() => setLoading(false))
  }, [selectedDoc])

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Reference</p>
          <h1 className="text-3xl font-semibold text-foreground">Documentation</h1>
          <p className="text-sm text-muted-foreground">
            Guides and manuals for administrators and tour operators.
          </p>
        </div>
        {selectedDoc && (
          <Button variant="outline" onClick={() => setSelectedDoc(null)}>
            <ChevronRight className="mr-2 h-4 w-4 rotate-180" />
            Back to all docs
          </Button>
        )}
      </header>

      {!selectedDoc ? (
        // Document List View
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {DOCS.map((doc) => (
            <Card
              key={doc.id}
              className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md"
              onClick={() => setSelectedDoc(doc)}
            >
              <CardHeader className="pb-2">
                <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-lg">{doc.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{doc.description}</p>
                <div className="mt-4 flex items-center text-sm font-medium text-primary">
                  Read guide
                  <ChevronRight className="ml-1 h-4 w-4" />
                </div>
              </CardContent>
            </Card>
          ))}

          {DOCS.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
              <BookOpen className="mb-4 h-12 w-12 text-muted-foreground/50" />
              <h3 className="text-lg font-semibold text-foreground">No documentation yet</h3>
              <p className="text-sm text-muted-foreground">
                Documentation will appear here as it&apos;s added.
              </p>
            </div>
          )}
        </div>
      ) : (
        // Document Content View
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>{selectedDoc.title}</CardTitle>
                <p className="text-sm text-muted-foreground">{selectedDoc.description}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-destructive">{error}</p>
                <Button variant="outline" className="mt-4" onClick={() => setSelectedDoc(selectedDoc)}>
                  Try again
                </Button>
              </div>
            ) : (
              <div className="prose prose-slate dark:prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Style headings with id for anchor links
                    h1: ({ children }) => {
                      const id = String(children).toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
                      return (
                        <h1 id={id} className="mb-4 mt-8 border-b pb-2 text-3xl font-bold first:mt-0">{children}</h1>
                      )
                    },
                    h2: ({ children }) => {
                      const id = String(children).toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
                      return (
                        <h2 id={id} className="mb-3 mt-8 text-2xl font-semibold text-foreground">{children}</h2>
                      )
                    },
                    h3: ({ children }) => {
                      const id = String(children).toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
                      return (
                        <h3 id={id} className="mb-2 mt-6 text-xl font-semibold text-foreground">{children}</h3>
                      )
                    },
                    h4: ({ children }) => {
                      const id = String(children).toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
                      return (
                        <h4 id={id} className="mb-2 mt-4 text-lg font-semibold text-foreground">{children}</h4>
                      )
                    },
                    // Style paragraphs
                    p: ({ children }) => (
                      <p className="mb-4 leading-7 text-foreground/90">{children}</p>
                    ),
                    // Style lists
                    ul: ({ children }) => (
                      <ul className="mb-4 ml-6 list-disc space-y-1">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="mb-4 ml-6 list-decimal space-y-1">{children}</ol>
                    ),
                    li: ({ children }) => (
                      <li className="text-foreground/90">{children}</li>
                    ),
                    // Style code blocks
                    code: ({ className, children }) => {
                      const isBlock = className?.includes('language-')
                      if (isBlock) {
                        return (
                          <code className="block overflow-x-auto rounded-lg bg-muted p-4 text-sm">
                            {children}
                          </code>
                        )
                      }
                      return (
                        <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-medium">
                          {children}
                        </code>
                      )
                    },
                    pre: ({ children }) => (
                      <pre className="mb-4 overflow-hidden rounded-lg">{children}</pre>
                    ),
                    // Style tables
                    table: ({ children }) => (
                      <div className="mb-4 overflow-x-auto">
                        <table className="w-full border-collapse text-sm">{children}</table>
                      </div>
                    ),
                    thead: ({ children }) => (
                      <thead className="border-b bg-muted/50">{children}</thead>
                    ),
                    th: ({ children }) => (
                      <th className="px-4 py-2 text-left font-semibold">{children}</th>
                    ),
                    td: ({ children }) => (
                      <td className="border-b px-4 py-2">{children}</td>
                    ),
                    // Style blockquotes
                    blockquote: ({ children }) => (
                      <blockquote className="mb-4 border-l-4 border-primary/30 pl-4 italic text-muted-foreground">
                        {children}
                      </blockquote>
                    ),
                    // Style horizontal rules
                    hr: () => <hr className="my-8 border-border" />,
                    // Style strong/bold
                    strong: ({ children }) => (
                      <strong className="font-semibold text-foreground">{children}</strong>
                    ),
                    // Style links - handle anchor links vs external links
                    a: ({ href, children }) => {
                      const isAnchor = href?.startsWith('#')
                      return (
                        <a
                          href={href}
                          className="font-medium text-primary underline-offset-4 hover:underline"
                          {...(!isAnchor && {
                            target: '_blank',
                            rel: 'noopener noreferrer'
                          })}
                        >
                          {children}
                        </a>
                      )
                    }
                  }}
                >
                  {content}
                </ReactMarkdown>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
